import { App, type URLOpenListenerEvent } from "@capacitor/app";

/**
 * 네이티브 appUrlOpen 디스패처. 플러그인 리스너는 앱 수명 동안 하나만 두고, 호스트(`hamlak://<host>/...`)별 라우트에 나눠 준다.
 * 스킴은 flavor 별(prod `hamlak` · dev `hamlak-dev`, AC0-155). OS 는 이 앱이 등록한 스킴만 넘겨 주므로 둘 다 받는다.
 * - `auth`  → auth.native.ts가 Apple 로그인 호출당 1회 등록·해제(auth-session 3.8).
 * 라우트 핸들러가 true를 돌려주면 소비된 것으로 보고 나머지 핸들러는 부르지 않는다.
 * 어느 라우트도 소비하지 않은 앱 스킴 URL과 https 앱 링크(App Links·Universal Links)는 폴백 하나(`setAppUrlFallback`)로 간다 —
 * 앱 셸의 딥링크 라우팅(AC0-261, app.native onOpenUrl → app/deepLink.ts). 폴백이 없으면 무시.
 */
export type AppUrlHandler = (url: URL) => boolean | void;

export const APP_SCHEMES = ["hamlak", "hamlak-dev"] as const;

const routes = new Map<string, Set<AppUrlHandler>>();
let fallback: AppUrlHandler | null = null;
let installed: Promise<void> | null = null;

function ensureListener(): Promise<void> {
  installed ??= App.addListener("appUrlOpen", (e: URLOpenListenerEvent) => {
    dispatchAppUrl(e.url);
  }).then(() => undefined);
  return installed;
}

/** URL 문자열 하나를 라우트에 전달한다(리스너·테스트 공용). 소비되면 true. */
export function dispatchAppUrl(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (APP_SCHEMES.some((s) => url.protocol === `${s}:`)) {
    // `hamlak://auth/apple` → host 'auth'. (URL 파서가 host를 비우는 환경 대비 pathname 첫 조각도 본다)
    const host = url.host || url.pathname.replace(/^\/+/, "").split("/")[0] || "";
    for (const h of [...(routes.get(host) ?? [])]) if (h(url) === true) return true;
  } else if (url.protocol !== "https:") {
    return false;
  }
  return fallback?.(url) === true;
}

/** 폴백 등록(하나만, 마지막 등록이 이긴다). 반환 함수로 해제한다. 첫 등록 때 플러그인 리스너를 붙인다. */
export function setAppUrlFallback(handler: AppUrlHandler): () => void {
  fallback = handler;
  void ensureListener();
  return () => {
    if (fallback === handler) fallback = null;
  };
}

/** 라우트 등록. 반환 함수로 해제한다. 첫 등록 때 플러그인 리스너를 붙인다. */
export function registerAppUrlRoute(host: string, handler: AppUrlHandler): () => void {
  let set = routes.get(host);
  if (!set) routes.set(host, (set = new Set()));
  set.add(handler);
  void ensureListener();
  return () => {
    set.delete(handler);
    if (set.size === 0) routes.delete(host);
  };
}
